import { NEEDS_ATTENTION_BY_USER_KEYS } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-status';
import { Heading } from '@efdigital/ll-addon-ui';
import type { QuotaPoolSummary } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import { OnboardingChecklist } from '@efdigital/ll-xceed-v2/components/coaching/coachee/onboarding-checklist';
import { QuickStartLink } from '@efdigital/ll-xceed-v2/components/coaching/quick-start-link.gts';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type { CoachingCoacheeCohortIndexModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/index';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type SessionService from '@efdigital/ll-xceed-v2/services/session';
import type { TOC } from '@ember/component/template-only';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

interface IndexSignature {
  Args: {
    model: CoachingCoacheeCohortIndexModel;
    controller: unknown;
  };
}

const MAX = 20;

function creditPercentage(remaining: number, total: number): number {
  return total ? Math.round((remaining / total) * 100) : 0;
}

function creditPercentageStyle(remaining: number, total: number): string {
  return `width: ${creditPercentage(remaining, total)}%`;
}

const PercentageBar = <template>
  <div class="card shadow-sm mb-4 border-0 rounded-4">
    <div
      class="card-body d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-4 p-3 p-md-4"
    >
      <div class="flex-shrink-0">
        <p
          class="small text-body-secondary fw-semibold mb-1"
        >{{@balanceLabel}}</p>
        <p class="mb-1">
          <span class="display-4 fw-bold lh-1">{{@quota.remaining}}</span>
          <span class="fs-4 text-body-secondary">/
            {{@quota.allocated}}</span>
        </p>
        <p class="small text-body-secondary mb-0">{{@remainingLabel}}</p>
      </div>
      <div class="flex-grow-1 px-0 px-md-4">
        <div
          class="progress mb-3 rounded-pill"
          role="progressbar"
          aria-valuenow={{@quota.remaining}}
          aria-valuemin="0"
          aria-valuemax={{@quota.allocated}}
          aria-label="{{@remainingLabel}}"
        >
          <div
            class="progress-bar bg-primary rounded-pill"
            style={{creditPercentageStyle @quota.remaining @quota.allocated}}
          ></div>
        </div>
        <div class="d-flex flex-wrap gap-3 gap-md-4">
          <span
            class="d-inline-flex align-items-center gap-2 fw-bold text-body"
          >
            <iconify-icon
              inline
              icon="ph:circle-fill"
              class="text-primary"
              aria-hidden="true"
            />
            {{@quota.remaining}}
            {{@remainingLabel}}
          </span>
          {{#if @upcomingCount}}
            <span
              class="d-inline-flex align-items-center gap-2 text-body-secondary"
            >
              <iconify-icon
                inline
                icon="ph:circle-fill"
                class="text-warning"
                aria-hidden="true"
              />
              {{@upcomingCount}}
              pending
            </span>
          {{/if}}
        </div>
      </div>
    </div>
  </div>
</template> satisfies TOC<{
  Args: {
    quota: QuotaPoolSummary;
    upcomingCount: number;
    balanceLabel: string;
    remainingLabel: string;
  };
}>;

export default class CoachingCoacheeCohortIndexComponent extends Component<IndexSignature> {
  @service declare coach: CoachService;
  @service declare store: Store;
  @service declare session: SessionService;

  async getUpcomingChemistrySessions() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoachingPlan(this.args.model.coachingPlan!.id!, [
        'scheduled',
      ])
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
      next: sessions.content.data[0],
    };
  }

  async getAttentionNeeded() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoachingPlan(
        this.args.model.coachingPlan!.id!,
        NEEDS_ATTENTION_BY_USER_KEYS
      )
    );
    return {
      count: sessions.content.data.length,
    };
  }

  getStats = async () => {
    const [upcomingChemistrySessions, attentionNeeded] = await Promise.all([
      this.getUpcomingChemistrySessions(),
      this.getAttentionNeeded(),
    ]);

    return {
      upcomingChemistrySessions,
      attentionNeeded,
    };
  };

  <template>
    <Await @promise={{(this.getStats)}}>
      <:success as |stats|>

        <div class="container py-3">
          <Heading
            @size="lg"
            @text="Welcome, {{this.session.cached.displayName}}"
            @secondary="Here's your coaching journey at a glance."
            @class="mb-4"
          />

          <section class="mb-4">
            <div class="row g-3">
              <div class="col-md-4">
                <QuickStartLink
                  @route="coaching.coachee.cohort.help.how-coaching-works"
                  @icon="ph:lightbulb"
                  @title="How coaching works here"
                  @description="Learn about engagements, session credits, and chemistry calls."
                  class="h-100"
                />
              </div>
              <div class="col-md-4">
                <QuickStartLink
                  @route="coaching.coachee.cohort.help.choosing-a-coach"
                  @icon="ph:user-circle"
                  @title="Choosing the right coach"
                  @description="Tips for reviewing profiles and making your shortlist."
                  class="h-100"
                />
              </div>
              <div class="col-md-4">
                <QuickStartLink
                  @route="coaching.coachee.cohort.help.coaching-handbook"
                  @icon="ph:notebook"
                  @title="Coaching handbook"
                  @description="Session credits, scheduling rules and support contacts."
                  class="h-100"
                />
              </div>
            </div>
          </section>

          <OnboardingChecklist
            @hasPublishedAnswer={{@model.hasPublishedAnswer}}
            @planner={{@model.planner}}
            class="mb-4"
          />

          {{#if @model.planner.coachHasBeenChosen}}
            <PercentageBar
              @quota={{@model.planner.quota.coaching}}
              @upcomingCount={{stats.upcomingChemistrySessions.count}}
              @balanceLabel="Hours"
              @remainingLabel="hours remaining"
            />
          {{else}}
            <PercentageBar
              @quota={{@model.planner.quota.chemistry}}
              @upcomingCount={{stats.upcomingChemistrySessions.count}}
              @balanceLabel="Chemistry calls"
              @remainingLabel="chemistry calls remaining"
            />
          {{/if}}
        </div>
      </:success>
    </Await>
  </template>
}
